import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import axios from 'axios'
import applyConverters from 'axios-case-converter'
import LogoShort from 'assets/images/bp-logo-orange.png'
import MarkdownRenderer from 'components/MarkdownRenderer'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import { apiUrl } from 'utils/apiUrl'

// Lightweight, no-auth axios instance for the public share endpoint.
// We don't reuse APIService() because that one carries cookies, XSRF, and
// auth-redirect interceptors that don't apply on a public page. `apiUrl`
// already ends in `/api`, so request paths must NOT re-prefix it.
const publicApi = applyConverters(axios.create({ baseURL: apiUrl }))

const formatDateTime = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Public, no-auth view of a Guru Q&A share. Reached via /share/guru/:token.
 * The token is the `share_token` issued by the publish endpoint.
 *
 * Backend pre-signs each citation's URL so the viewer can open the source
 * document directly from S3 without needing a BP account.
 */
const isPdfName = (name) => typeof name === 'string' && name.toLowerCase().endsWith('.pdf')

const GuruPublicSharePage = () => {
  const { token } = useParams()
  const [share, setShare] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [openingId, setOpeningId] = useState(null)
  // Per-citation open errors are kept separate from the page-level `error`
  // so a missing source doesn't blow away the whole share view.
  const [sourceError, setSourceError] = useState(null)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    const load = async () => {
      try {
        const res = await publicApi.get(`/external-access/guru-shares/${token}`)
        if (!cancelled) setShare(res?.data?.data || null)
      } catch (e) {
        if (!cancelled) {
          const status = e?.response?.status
          if (status === 404) setError('This shared Q&A is no longer available.')
          else if (status === 429) setError('Too many requests. Please try again in a moment.')
          else setError('Could not load this shared Q&A.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [token])

  // Click handler: log the per-source view, fetch a fresh pre-signed URL,
  // and open the file inline. PDFs render in PdfFullScreenViewer; anything
  // else opens in a new tab using the freshly-signed URL.
  const openSource = async (citation) => {
    if (!citation?.id || openingId) return
    setOpeningId(citation.id)
    try {
      const res = await publicApi.post(`/external-access/guru-shares/${token}/sources/${citation.id}/view`)
      const fresh = res?.data?.data || {}
      const url = fresh.url || citation.url
      const name = fresh.name || citation.name
      if (isPdfName(name)) {
        setPreviewFile({
          id: citation.id,
          displayName: name,
          url,
          originalUrl: fresh.originalUrl || null,
          contentType: 'application/pdf',
        })
      } else {
        // Non-PDFs (images, office docs) — open the fresh URL in a new tab.
        // We've still logged the view server-side via the POST above.
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } catch (e) {
      const status = e?.response?.status
      if (status === 404) {
        // Either share revoked/expired between page load and click, or the
        // file was deleted on the source BP. Surface as a per-source banner
        // — the rest of the share is still readable so we keep it visible.
        setSourceError(`"${citation.name}" is no longer available.`)
      } else if (status === 410) {
        setSourceError('This shared Q&A has been revoked or expired. Refresh the page to confirm.')
      } else if (citation.url && !isPdfName(citation.name)) {
        // Soft fallback on transient error: open the cached pre-signed URL
        // so the click still works. The audit log is best-effort.
        window.open(citation.url, '_blank', 'noopener,noreferrer')
      } else {
        setSourceError(`Could not open "${citation.name}". Please try again.`)
      }
    } finally {
      setOpeningId(null)
    }
  }

  return (
    <Page>
      <Content>
        {loading && <Card>Loading shared Q&amp;A…</Card>}

        {!loading && error && <Card $error>{error}</Card>}

        {!loading && !error && share && (
          <>
            {sourceError && (
              <SourceErrorBanner role="alert">
                <span>{sourceError}</span>
                <DismissBtn type="button" onClick={() => setSourceError(null)} aria-label="Dismiss">
                  ×
                </DismissBtn>
              </SourceErrorBanner>
            )}
            <Header>
              <HeaderTitle>Shared from Guru chat</HeaderTitle>
              <HeaderMeta>
                {share.scope === 'portfolio' && share.portfolio?.name ? (
                  <>
                    Portfolio: <strong>{share.portfolio.name}</strong>
                    {share.portfolio.buildingCount ? ` (${share.portfolio.buildingCount} Building Passport${share.portfolio.buildingCount === 1 ? '' : 's'})` : ''}
                    {' · '}
                  </>
                ) : share.building?.name ? (
                  <>Building Passport: <strong>{share.building.name}</strong> · </>
                ) : null}
                {share.sharedByName && <>Shared by {share.sharedByName} · </>}
                {formatDateTime(share.createdAt)}
              </HeaderMeta>
              {share.expiresAt && (
                <ExpiryNote>This link expires on {formatDateTime(share.expiresAt)}.</ExpiryNote>
              )}
            </Header>

            <QuestionCard>
              <BubbleLabel>Question</BubbleLabel>
              <BubbleBody>{share.question}</BubbleBody>
            </QuestionCard>

            <AnswerCard>
              <AnswerHeader>
                <GuruAvatar src={LogoShort} alt="Guru" />
                <BubbleLabel>Guru&rsquo;s answer</BubbleLabel>
              </AnswerHeader>
              <AnswerBody>
                <MarkdownRenderer content={share.answer} />
              </AnswerBody>

              {share.citations?.length > 0 && (
                <Section>
                  <SectionLabel>Sources ({share.citations.length})</SectionLabel>
                  <SourceList>
                    {share.citations.map((c, i) => {
                      const isPdf = isPdfName(c.name)
                      const isBusy = openingId === c.id
                      return (
                        <SourceRow
                          key={c.id || i}
                          type="button"
                          onClick={() => openSource(c)}
                          disabled={isBusy}
                          aria-label={`Open source ${c.name}`}
                        >
                          <SourceName>{c.name}</SourceName>
                          {isBusy ? (
                            <SourceHint>Opening…</SourceHint>
                          ) : isPdf ? (
                            <VisibilityOutlinedIcon style={{ fontSize: 14, opacity: 0.6 }} />
                          ) : (
                            <OpenInNewIcon style={{ fontSize: 14, opacity: 0.6 }} />
                          )}
                        </SourceRow>
                      )
                    })}
                  </SourceList>
                </Section>
              )}

              {share.webSources?.length > 0 && (
                <Section>
                  <SectionLabel>Web sources consulted</SectionLabel>
                  <WebList>
                    {share.webSources.map((domain, i) => (
                      <WebChip
                        key={i}
                        href={`https://${domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {domain}
                      </WebChip>
                    ))}
                  </WebList>
                </Section>
              )}

              <Disclaimer>
                AI-generated response — may not fully reflect the underlying documents.
                Always verify critical information.
              </Disclaimer>
            </AnswerCard>
          </>
        )}
      </Content>

      {previewFile && (
        <PdfFullScreenViewer
          open
          onClose={() => setPreviewFile(null)}
          file={previewFile}
          skipAccessLog
        />
      )}
    </Page>
  )
}

export default GuruPublicSharePage

const Page = styled.div`
  min-height: 100%;
`

const Content = styled.div`
  max-width: 880px;
  margin: 0 auto;
  @media (max-width: 768px) { padding: 0 4px; }
`

const Card = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 24px;
  font-size: 14px;
  color: ${({ $error }) => ($error ? '#b45309' : '#475569')};
  text-align: ${({ $error }) => ($error ? 'center' : 'left')};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`

const Header = styled.div`
  margin-bottom: 18px;
`

const HeaderTitle = styled.h1`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
`

const HeaderMeta = styled.div`
  font-size: 12px;
  color: #64748b;
`

const ExpiryNote = styled.div`
  margin-top: 6px;
  font-size: 12px;
  color: #b45309;
`

const QuestionCard = styled(Card)`
  margin-bottom: 14px;
  background: #fafbfc;
`

const AnswerCard = styled(Card)`
  margin-bottom: 14px;
`

const BubbleLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 8px;
`

const BubbleBody = styled.div`
  font-size: 15px;
  line-height: 1.55;
  color: #0f172a;
  white-space: pre-wrap;
`

const AnswerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`

const GuruAvatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 6px;
`

const AnswerBody = styled.div`
  font-size: 15px;
  line-height: 1.55;
  color: #0f172a;
`

const Section = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
`

const SectionLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 8px;
`

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SourceRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #f1f5f9; }
  &:disabled { opacity: 0.7; cursor: progress; }
`

const SourceHint = styled.span`
  font-size: 11px;
  color: #64748b;
  flex-shrink: 0;
`

const SourceErrorBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  font-size: 13px;
  color: #92400e;
`

const DismissBtn = styled.button`
  background: transparent;
  border: none;
  color: #92400e;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  &:hover { color: #78350f; }
`

const SourceName = styled.div`
  font-size: 13px;
  color: #0f172a;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const WebList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const WebChip = styled.a`
  font-size: 12px;
  color: #33becc;
  background: #e6f7f9;
  padding: 2px 10px;
  border-radius: 12px;
  text-decoration: none;
  &:hover { background: #bbdefb; }
`

const Disclaimer = styled.div`
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
`
